import { useRecoilCallback } from 'recoil'
import { useTranslation } from 'react-i18next'
import type { ApplicationType, Content } from '@messenger/types'
import {
  editorAccessLostPromptAtom,
  editorPagesAtom,
  unsavedChangesAtom,
} from '@/atoms/_editorAtoms'
import {
  editorRemintedItemIdsAtom,
  editorSelfDeletedPageIdsAtom,
} from '@/atoms/editorTextBoxAtoms'
import { updateOrderPage } from '@/api/orderPages'
import { useDropEditorTextBoxWork } from '@/hooks/editor/useDropEditorTextBoxWork'
import { useEditorSaveTextBoxes } from '@/hooks/editor/useEditorSaveTextBoxes'
import { useEditorSaveGesture } from '@/hooks/editor/useEditorSaveGesture'
import { isOrderPageGoneError } from '@/utils/editor/orderPageGone'
import { isEditAccessLostError } from '@/utils/editor/editAccessLost'
import { isOrderPageTurnedOverError } from '@/utils/editor/orderPageTurnedOver'
import { useSetToast } from 'hooks/utility/useSetToast'
import { useErrorHandler } from 'hooks/utility/useErrorHandler'
import { logger, EventCategory } from 'utils/logger'
import { useRecoilLogData } from 'utils/recoilLogger'
import { logWipeProvenance } from '@/utils/editor/wipeProvenance'

export type PageFanoutFailure = { pageNumber: number; error: unknown }

export type PageFanoutResult = {
  savedPageNumbers: number[]
  failedPageNumbers: number[]
  failures: PageFanoutFailure[]
}

type PatchPage = (
  page: Content,
  pageNumber: number,
) => Promise<{ status: number }>

export const runOrderPagePatchFanout = async ({
  pages,
  patchPage,
  onPageError,
}: {
  pages: Content[]
  patchPage: PatchPage
  onPageError?: (error: unknown, pageNumber: number) => void
}): Promise<PageFanoutResult> => {
  const savedPageNumbers: number[] = []
  const failedPageNumbers: number[] = []
  const failures: PageFanoutFailure[] = []
  await Promise.all(
    pages.map(async (page, index) => {
      const pageNumber = index + 1
      try {
        const { status } = await patchPage(page, pageNumber)
        if (status === 200) {
          savedPageNumbers.push(pageNumber)
        }
      } catch (error) {
        failures.push({ pageNumber, error })
        failedPageNumbers.push(pageNumber)
        onPageError?.(error, pageNumber)
      }
    }),
  )
  return { savedPageNumbers, failedPageNumbers, failures }
}

export const useCommitEditorPages = () => {
  const { t } = useTranslation()
  const setToast = useSetToast()
  const raiseAccessLostPrompt = useRecoilCallback(
    ({ set }) =>
      () =>
        set(editorAccessLostPromptAtom, true),
    [],
  )
  const handleError = useErrorHandler('Editor')
  const recoilLogData = useRecoilLogData()
  const saveEditorTextBoxes = useEditorSaveTextBoxes()
  const runSaveGesture = useEditorSaveGesture()
  const dropTextBoxWorkForPages = useDropEditorTextBoxWork()
  const readRemintedItemIds = useRecoilCallback(
    ({ snapshot }) =>
      () =>
        snapshot.getLoadable(editorRemintedItemIdsAtom).valueMaybe(),
    [],
  )

  // A load that re-initialised the editor during this Save wrote a record of
  // its own, whose ids this Save never wrote; only the record the Save began
  // under is cleared.
  const forgetRemintedItemIds = useRecoilCallback(
    ({ set }) =>
      (baselineRecord: unknown) => {
        set(editorRemintedItemIdsAtom, (current) =>
          current === baselineRecord ? {} : current,
        )
      },
    [],
  )

  const readSelfDeletedPageIds = useRecoilCallback(
    ({ snapshot }) =>
      () =>
        snapshot.getLoadable(editorSelfDeletedPageIdsAtom).valueMaybe() ?? {},
    [],
  )

  const readEditorPages = useRecoilCallback(
    ({ snapshot }) =>
      () =>
        snapshot.getLoadable(editorPagesAtom).valueMaybe() as
          | Content[]
          | undefined,
    [],
  )

  // Reference compare is complete: every Recoil write replaces the array identity.
  const clearUnsavedIfStillCurrent = useRecoilCallback(
    ({ snapshot, set }) =>
      (baselinePages: unknown) => {
        const livePages = snapshot.getLoadable(editorPagesAtom).valueMaybe()
        if (livePages === baselinePages) {
          set(unsavedChangesAtom, false)
          return true
        }
        return false
      },
    [],
  )

  const commitEditorPages = async ({
    flushCanvas,
    applicationType,
    updatePageSetPointer,
    onStalePages,
    onTurnedOverPages,
  }: {
    flushCanvas: () => Promise<Content[] | undefined | void>
    applicationType: ApplicationType
    updatePageSetPointer?: () => Promise<void>
    /** Leave the editor: the pages this tab holds are gone. */
    onStalePages?: () => void
    /**
     * Ask for a reload: the page set was turned over to Lotus text boxes
     * after this tab loaded it, so its pages cannot be written back.
     */
    onTurnedOverPages?: () => void
  }): Promise<void> => {
    // One message and one exit, whichever route reported the pages gone.
    const handleStalePages = (stalePageIds: readonly string[]) => {
      dropTextBoxWorkForPages(stalePageIds)
      setToast({ text: t('toast.editorPagesReplaced') })
      logger.info({
        message: 'editor_save_pages_replaced_elsewhere',
        category: EventCategory.SAVE,
        data: { ...recoilLogData, stalePageIds: [...stalePageIds] },
      })
      onStalePages?.()
    }

    // Two triggers in one tick — a double click, or the sidebar save landing
    // beside the Save button — both read the saving atom as false, because a
    // Recoil write is only visible to a later read once React has committed
    // it, and the Save button's disabled attribute is a render behind it too.
    // The gesture below takes the tab's save latch in one statement, so the
    // second trigger loses it and never runs this body.
    await runSaveGesture(
      'editor_commit',
      applicationType,
      async ({ versionNumber: coveringVersionNumber }) => {
        let flushed: Content[] | undefined | void
        try {
          flushed = await flushCanvas()
        } catch (error) {
          handleError((error as Error)?.message, error as Error)
          return
        }

        // The pages sent are the ones the flush wrote. A snapshot taken here
        // still reads the store's current tree (Recoil 0.5, `cloneSnapshot`),
        // and the flush's write sits in the next tree until React commits, so
        // the atom read after the flush is the canvas as it stood before it:
        // every edit since the last sync would miss this Save and land on the
        // next. The atom is only the fallback for a flush that wrote nothing.
        const pages = Array.isArray(flushed)
          ? flushed
          : (readEditorPages() ?? [])
        const baselinePages = pages
        const baselineRemintedItemIds = readRemintedItemIds()

        // Every page write of this Save names the version the gesture took
        // before any of this ran; a Save that got no number records a version
        // per page write instead.
        const textBoxes = await saveEditorTextBoxes()
        // The page JSON carries only a stub per Lotus box; the words live in
        // the rows the save above writes, and a stub whose row never landed
        // is dropped on the next load. So the pages are not written over rows
        // that did not save: the drafts stay, the guard stays armed, and the
        // next Save sends both. The layout drawers refuse the same way. The
        // text-box save has already toasted its failure or conflict, or
        // raised the access-lost prompt, and a second toast in the same tick
        // replaces it (useSetToast), so this refusal only logs.
        if (textBoxes.conflicted || textBoxes.failed) {
          logger.info({
            message: 'editor_save_refused_text_boxes_unsaved',
            category: EventCategory.SAVE,
            data: {
              ...recoilLogData,
              conflicted: textBoxes.conflicted,
              failed: textBoxes.failed,
              totalPages: pages.length,
            },
          })
          return
        }

        // A page that is gone takes its own edits with it, but the pages still
        // on the line saved their text boxes and their JSON belongs with them:
        // the tab leaves after they are written, not instead of writing them.
        const goneBeforeFanout = new Set(textBoxes.stalePageIds)
        const livePages = pages.filter(
          (page) => !goneBeforeFanout.has(String(page.product?.orderPageId)),
        )

        // OBSERVABILITY (log-only, the save below proceeds unchanged): sending
        // a zero-item payload for any page is the wire signature of the
        // production page wipes (backend audit: prevItemCount N -> 0). Emit
        // the full per-page item vector BEFORE the requests go out so a field
        // occurrence shows what the whole pages state looked like at send
        // time; correlate with the backend suspicious_page_wipe audit row via
        // page id + timestamp.
        const blankPages = pages.filter(
          (page) => (page?.product?.items?.length ?? 0) === 0,
        )
        if (blankPages.length > 0) {
          logWipeProvenance(
            'editor_save_sending_blank_page',
            'save is sending a zero-item payload for at least one page',
            {
              ...recoilLogData,
              writer: 'useCommitEditorPages.commitEditorPages',
              blankOrderPageIds: blankPages.map(
                (page) => page?.product?.orderPageId,
              ),
              pageItemVector: pages.map(
                (page) => page?.product?.items?.length ?? -1,
              ),
              totalPages: pages.length,
            },
          )
        }

        const stalePageIds: string[] = [...textBoxes.stalePageIds]
        const selfDeleted = readSelfDeletedPageIds()
        // A page this tab deleted while the Save was in flight: its write
        // comes back page-gone, and that is neither a change made elsewhere
        // nor a failed save, so it counts toward nothing below.
        const selfDeletedPageNumbers = new Set<number>()
        const turnedOverPageIds: string[] = []
        const accessLostPageIds: string[] = []
        const result = await runOrderPagePatchFanout({
          pages: livePages,
          patchPage: (page) =>
            updateOrderPage(
              String(page.product?.orderPageId),
              {
                jsonTemplate: JSON.stringify(page),
                applicationType,
                ...(coveringVersionNumber !== undefined && {
                  coveringVersionNumber,
                }),
              },
              applicationType,
            ),
          onPageError: (error, pageNumber) => {
            if (isOrderPageGoneError(error)) {
              const pageId = String(
                livePages[pageNumber - 1]?.product?.orderPageId ?? '',
              )
              // A page this tab deleted itself is not a change made elsewhere.
              // One message for the whole gesture otherwise, sent once the
              // fan-out has settled — a per-page toast would show one per page.
              if (pageId && selfDeleted[pageId] === true)
                selfDeletedPageNumbers.add(pageNumber)
              else if (pageId) stalePageIds.push(pageId)
              return
            }
            if (isOrderPageTurnedOverError(error)) {
              turnedOverPageIds.push(
                String(livePages[pageNumber - 1]?.product?.orderPageId ?? ''),
              )
              return
            }
            if (isEditAccessLostError(error)) {
              accessLostPageIds.push(
                String(livePages[pageNumber - 1]?.product?.orderPageId ?? ''),
              )
              return
            }
            const err = error as {
              response?: { data?: { message?: string } }
              message?: string
            }
            handleError(
              err?.response?.data?.message || err?.message || 'save failed',
              error as Error,
            )
          },
        })

        if (stalePageIds.length > 0) {
          handleStalePages(stalePageIds)
          return
        }

        // The edits stay on the canvas and the unsaved flag stays set, so the
        // person can copy text out; the prompt offers the reload, and every
        // later Save from this tab is refused and raises it again.
        if (turnedOverPageIds.length > 0) {
          logger.info({
            message: 'editor_save_refused_page_set_turned_over',
            category: EventCategory.SAVE,
            data: {
              ...recoilLogData,
              turnedOverPageIds,
              savedPages: result.savedPageNumbers,
            },
          })
          onTurnedOverPages?.()
          return
        }

        // No page was written and this tab cannot write again under its
        // link: one prompt for the gesture, and the edits and the unsaved flag
        // stay on the canvas so the person can copy text out. A Save that
        // wrote some pages reports the rest as unsaved below.
        if (
          accessLostPageIds.length > 0 &&
          result.savedPageNumbers.length === 0
        ) {
          logger.info({
            message: 'editor_save_refused_edit_access_lost',
            category: EventCategory.SAVE,
            data: {
              ...recoilLogData,
              accessLostPageIds,
              savedPages: result.savedPageNumbers,
            },
          })
          raiseAccessLostPrompt()
          return
        }

        await updatePageSetPointer?.()

        const failedPageNumbers = result.failedPageNumbers.filter(
          (pageNumber) => !selfDeletedPageNumbers.has(pageNumber),
        )
        const totalPages = livePages.length - selfDeletedPageNumbers.size
        const allPagesSaved =
          result.savedPageNumbers.length === totalPages &&
          failedPageNumbers.length === 0

        if (allPagesSaved) {
          const cleared = clearUnsavedIfStillCurrent(baselinePages)
          // Every page is written, so the stored JSON now holds each id the
          // load minted.
          forgetRemintedItemIds(baselineRemintedItemIds)
          setToast({ text: t('toast.productSaved') })
          logger.info({
            message: 'editor_changes_saved_successfully',
            category: EventCategory.SAVE,
            data: {
              ...recoilLogData,
              jsonTemplate: JSON.stringify(pages),
              savedPages: result.savedPageNumbers,
              totalPages,
              unsavedChangesCleared: cleared,
            },
          })
        }

        if (failedPageNumbers.length > 0) {
          setToast({
            text: t('toast.savePageError', {
              error: failedPageNumbers.join(', '),
            }),
          })
          logger.error({
            message: 'editor_save_failed',
            category: EventCategory.ERROR,
            error: new Error('Failed to save pages'),
            data: {
              ...recoilLogData,
              jsonTemplate: JSON.stringify(pages),
              failedPages: failedPageNumbers,
              totalPages,
              savedPages: result.savedPageNumbers,
            },
          })
        }
      },
    )
  }

  return { commitEditorPages }
}

export default useCommitEditorPages
